export const normalizeMultilineText = (
     text?: string,
     fallback = "No remarks"
    ) => {
     const cleaned = text?.split(/\r?\n/).filter(line => line.trim()).join("\n");
     return cleaned || fallback;
    };

/**
 * Splits `text` (result) into segments for highlighting against `rawQuery` (search).
 * Matches search characters in order against the result (case-insensitive). Extra characters
 * in the result (e.g. "1" in CN1 vs CNAA) are skipped without consuming search characters.
 */
export function splitTextByOrderedSearchMatch(
  text: string,
  rawQuery: string,
): Array<{ text: string; highlight: boolean }> {
  const q = rawQuery.trim();
  const t = text ?? "";
  if (!q) return [{ text: t, highlight: false }];
  if (!t) return [{ text: "", highlight: false }];

  const highlight = new Array<boolean>(t.length).fill(false);
  let si = 0;
  let ri = 0;

  while (si < q.length && ri < t.length) {
    if (q.charAt(si).toLowerCase() === t.charAt(ri).toLowerCase()) {
      highlight[ri] = true;
      si++;
      ri++;
    } else {
      ri++;
    }
  }

  const segments: Array<{ text: string; highlight: boolean }> = [];
  let i = 0;
  while (i < t.length) {
    const h = highlight[i];
    let j = i + 1;
    while (j < t.length && highlight[j] === h) j++;
    segments.push({ text: t.slice(i, j), highlight: h });
    i = j;
  }
  return segments;
}

/** Every case-insensitive occurrence of `q` in `text` as contiguous spans. */
function splitTextByAllCaseInsensitiveSubstrings(
  text: string,
  q: string,
): Array<{ text: string; highlight: boolean }> {
  const t = text ?? "";
  const lower = t.toLowerCase();
  const lowerQ = q.toLowerCase();
  const out: Array<{ text: string; highlight: boolean }> = [];
  let pos = 0;
  let idx = lower.indexOf(lowerQ, pos);
  while (idx !== -1) {
    if (idx > pos) out.push({ text: t.slice(pos, idx), highlight: false });
    out.push({ text: t.slice(idx, idx + q.length), highlight: true });
    pos = idx + q.length;
    idx = lower.indexOf(lowerQ, pos);
  }
  if (pos < t.length) out.push({ text: t.slice(pos), highlight: false });
  return out.length ? out : [{ text: t, highlight: false }];
}

/**
 * Highlight search in result text: contiguous suffix (e.g. `38013` at end of part number), then
 * prefix, then any substring occurrences, else ordered subsequence (skip extra chars).
 */
export function splitTextForEnquirySearchHighlight(
  text: string,
  rawQuery: string,
): Array<{ text: string; highlight: boolean }> {
  const t = text ?? "";
  const q = rawQuery.trim();
  if (!q) return [{ text: t, highlight: false }];
  if (!t) return [{ text: "", highlight: false }];

  const tL = t.toLowerCase();
  const qL = q.toLowerCase();

  if (tL.length >= qL.length && tL.endsWith(qL)) {
    const i = t.length - q.length;
    const segs: Array<{ text: string; highlight: boolean }> = [];
    if (i > 0) segs.push({ text: t.slice(0, i), highlight: false });
    segs.push({ text: t.slice(i), highlight: true });
    return segs;
  }

  if (tL.startsWith(qL)) {
    return [
      { text: t.slice(0, q.length), highlight: true },
      { text: t.slice(q.length), highlight: false },
    ];
  }

  if (tL.includes(qL)) {
    return splitTextByAllCaseInsensitiveSubstrings(t, q);
  }

  return splitTextByOrderedSearchMatch(t, q);
}

/**
 * Normalize for part-number style matching: ignore spaces, hyphens, slashes, underscores.
 * Prefix / suffix / substring on the raw part number are all satisfied by compact `pn.includes(q)`.
 */
export function normalizePartSearchKey(s: string): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s\-_/]+/g, "");
}

/**
 * True → no mismatch modal.
 * Part number: compare normalized strings so `09103`, `3m080`, `8013` all match `09103M08013`.
 * Part name: normalized substring, plus plain lowercase substring for spaced phrases.
 * Empty search → compatible.
 */
export function isPartNumberCompatibleWithSearch(
  rawQuery: string,
  partNumber: string,
  partName?: string,
): boolean {
  const qRaw = rawQuery.trim();
  if (!qRaw) return true;
  const q = normalizePartSearchKey(qRaw);
  if (!q) return true;

  const pnCompact = normalizePartSearchKey(String(partNumber ?? ""));
  const nameRaw = String(partName ?? "").trim().toLowerCase();
  const nameCompact = normalizePartSearchKey(String(partName ?? ""));

  if (!pnCompact && !nameRaw && !nameCompact) return false;

  if (pnCompact.includes(q)) return true;
  if (nameCompact.includes(q)) return true;
  if (nameRaw.includes(qRaw.toLowerCase())) return true;
  return false;
}